import { DateTime } from 'luxon';
import { DateParser, RecognitionMode, RecognitionResult } from './date-parser';

export class LuxonDateParser extends DateParser {
  private readonly delimiters: string[] = [' ', '.', ',', '-', '/'];
  private readonly russianMonthFixMap = [
    { bad: 'ЯНВ', fixed: DateTime.fromISO('2001-01-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'ФЕВ', fixed: DateTime.fromISO('2001-02-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'МАР', fixed: DateTime.fromISO('2001-03-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'АПР', fixed: DateTime.fromISO('2001-04-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'МАЙ', fixed: DateTime.fromISO('2001-05-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'ИЮН', fixed: DateTime.fromISO('2001-06-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'ИЮЛ', fixed: DateTime.fromISO('2001-07-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'АВГ', fixed: DateTime.fromISO('2001-08-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'СЕН', fixed: DateTime.fromISO('2001-09-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'ОКТ', fixed: DateTime.fromISO('2001-10-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'НОЯ', fixed: DateTime.fromISO('2001-11-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
    { bad: 'ДЕК', fixed: DateTime.fromISO('2001-12-01', { zone: 'UTC', locale: 'ru' }).toFormat('MMM').replace('.', '') },
  ];

  parse(input: string, locale: string, modes: RecognitionMode[]): RecognitionResult {
    // Т.к библиотека luxon имеет немного искаженный спискок
    // названий месяцев, потому на приходится поправлять некоторые из них.
    // В противном случае матчинг не происходит.

    input = this.fixShortMonthToken(input, locale).trim();

    const formats: string[] = [];
    const delimiter = this.delimiters.find((d) => input.includes(d));
    if (delimiter) {
      if (modes.includes(RecognitionMode.Y)) {
        formats.push('y');
      }
      if (modes.includes(RecognitionMode.MY)) {
        formats.push('MMM' + delimiter + 'y');
        formats.push('M' + delimiter + 'y');
      }
      if (modes.includes(RecognitionMode.DMY)) {
        formats.push('d' + delimiter + 'MMM' + delimiter + 'y');
        formats.push('d' + delimiter + 'M' + delimiter + 'y');
      }
    } else {
      if (modes.includes(RecognitionMode.Y) && input.length <= 4) {
        formats.push('y');
      }
      if (modes.includes(RecognitionMode.MY) && input.length === 6) {
        formats.push('My');
      }
      if (modes.includes(RecognitionMode.DMY) && input.length === 8) {
        formats.push('dMy');
      }
    }

    let result = new RecognitionResult();
    for (const format of formats) {
      const explainedFormat = DateTime.fromFormatExplain(input, format, { locale: locale });
      if (explainedFormat.matches && Object.keys(explainedFormat.matches).length > 0) {
        if (typeof explainedFormat.matches['y'] === 'number') {
          result.year = this.fixYearParameter(explainedFormat.matches['y']);
        }
        if (typeof explainedFormat.matches['M'] === 'number' && explainedFormat.matches['M']) {
          result.month = explainedFormat.matches['M'];
        }
        if (typeof explainedFormat.matches['d'] === 'number' && explainedFormat.matches['d']) {
          result.day = explainedFormat.matches['d'];
        }
        if (result.isSucceded()) {
          const _y = result.day < 1 && result.month < 1 && result.year > 0 && modes.includes(RecognitionMode.Y);
          const _my = result.day < 1 && result.month > 0 && result.year > 0 && modes.includes(RecognitionMode.MY);
          const _dmy = result.day > 0 && result.month > 0 && result.year > 0 && modes.includes(RecognitionMode.DMY);
          if (_y || _my || _dmy) {
            const val =
              (result.day > 0 ? result.day : 1) +
              '-' +
              (result.month > 0 ? result.month : 1) +
              '-' +
              (result.year > 0 ? result.year : 2001);
            if (DateTime.fromFormat(val, 'd-M-y').isValid) {
              break;
            }
          }
        }
        result = new RecognitionResult();
      }
    }
    return result;
  }

  private fixYearParameter(year: number): number {
    if (year >= 0 && year <= 99) {
      const currentYear = DateTime.local().year % 100;
      return year > currentYear ? 1900 + year : 2000 + year;
    }
    if (year > 9999) {
      return 9999;
    }
    return year;
  }

  private fixShortMonthToken(input: string, locale: string): string {
    input = input.toUpperCase();

    if (locale === 'ru') {
      this.russianMonthFixMap.forEach((record) => {
        input = input.replace(record.bad, record.fixed);
      });
    }

    return input;
  }
}
